import './index.css';

import { CalendarIcon, LinkIcon } from '@heroicons/react/outline';
import {
  appointmentService,
  assessmentService,
  providerService,
  statesService,
  userService,
} from '@/services';
import { useCallback, useContext, useEffect, useState } from 'react';

import Card from '@/components/Card';
import ConfirmModal from '@/modals/ConfirmModal';
import { GlobalContext } from '@/context';
import LoadingOverlay from '@/components/LoadingOverlay';
import STATE_ONBOARDING_STEPS from '@/constants/stateOnboardingSteps';
import StateOnboardingSteps from '@/components/StateOnboardingSteps';
import { USER_TYPES } from '@/constants';
import classNames from 'classnames';
import { convertToTimezone } from '@/utils';
import moment from 'moment';
import { useHistory } from 'react-router';

const TipsWidget = () => {
  const [tips, setTips] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const tipsRes = await userService.getCurrentUserTips();
        setTips(tipsRes);
      } catch (err) {
        console.log(err);
      }
    })();
  }, []);

  return (
    <Card title="Tips for your Appointment">
      <div className="flex flex-col space-y-6 py-3">
        {tips.map((tip, index) => (
          <div className="Item flex space-x-2 items-start" key={index}>
            <div className="mb-4 flex-shrink-0 sm:mb-0 sm:mr-4">
              <div className="rounded-full bg-cover h-10 w-10 bg-mmj-gray flex items-center justify-center text-2xl font-bold text-mmj-blue">
                {index + 1}
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold">{tip.label}</h4>
              <p className="text-sm text-gray-600 mb-2">{tip.text}</p>
              {tip.learnMore && (
                <a
                  href="#"
                  className="-ml-2 px-2 py-1.5 rounded-md text-sm font-medium text-mmj-blue hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-gray-600"
                >
                  Learn More
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

const StateQuickLinksWidget = () => {
  const LINKS = providerService.getStateLinks();

  return (
    <Card title="State Quick Links">
      <ul>
        {LINKS.map((_link) => (
          <li
            className="flex items-center space-x-3 mb-2"
            key={JSON.stringify(_link)}
          >
            <LinkIcon className="text-mmj-blue w-4 h-4" />
            <a
              className="text-mmj-blue underline"
              href={_link.url}
              target="_blank"
              rel="noreferrer"
            >
              {_link.label}
            </a>
          </li>
        ))}
      </ul>
    </Card>
  );
};

const LAYOUTS = {
  [USER_TYPES.provider.code]: {
    upcoming: { enabled: true },
    rows: [
      {
        cols: [
          {
            className: 'w-full lg:w-7/12 flex flex-col space-y-4',
            children: (
              <>
                <TipsWidget />
              </>
            ),
          },
          {
            className: 'w-full lg:flex-1 flex flex-col space-y-4',
            children: (
              <>
                <StateQuickLinksWidget />
              </>
            ),
          },
        ],
      },
    ],
  },
};

let appointmentPassedTimer;
const DashView = () => {
  const { userType, currentUser, notify } = useContext(GlobalContext);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssessmentConfirm, setShowAssessmentConfirm] = useState(false);
  // eslint-disable-next-line
  const history = useHistory();
  const checkIfPassed = useCallback(() => {
    if (!appointments || appointments.length === 0) {
      return;
    }

    if (
      moment.utc(appointments[0].appointment_end_time).local().valueOf() <
      new Date().valueOf()
    ) {
      setLoading(true);
      loadData();
    }
  }, [appointments]);
  useEffect(() => {
    loadData();

    appointmentPassedTimer = setInterval(() => {
      checkIfPassed();
    }, 1000);
    return () => {
      if (appointmentPassedTimer) {
        clearInterval(appointmentPassedTimer);
      }
    };
  }, []);

  const loadData = async () => {
    try {
      const params = {
        appointment_end_after: new Date().toISOString(),
        sort: 'appointment_start_time',
        order: 'ASC',
      };

      if (userType === USER_TYPES.patient.code) {
        params.patient_id = currentUser.uid;
      } else {
        params.provider_id = currentUser.uid;
      }

      const {
        data: { data },
      } = await appointmentService.listAppointments(params);
      setAppointments(data);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  };

  const onCancelAssessment = () => {
    setShowAssessmentConfirm(false);
    window.location.href = `/assessment/${currentUser.state}`;
  };

  const onConfirmAssessment = async () => {
    setShowAssessmentConfirm(false);
    setLoading(true);

    try {
      const { data: states } = await statesService.getAllStates({
        state: currentUser.state,
      });
      if (!states || states.length === 0) {
        throw new Error(
          'No state configuration found. Please contact to your administrator',
        );
      }

      const { assessment_template_id: templateId } = states[0];
      if (!templateId) {
        throw new Error(
          'No assessment template found. Please contact to your administrator',
        );
      }

      const {
        data: { data: assessmentResults },
      } = await assessmentService.getAllAssessmentResults({
        template_id: templateId,
        order: 'DESC',
        sort: 'created_at',
        per_page: 1,
      });
      if (
        !assessmentResults ||
        assessmentResults.length === 0 ||
        !assessmentResults[0].assessment_passed
      ) {
        window.location.href = `/assessment/${currentUser.state}`;
      } else {
        window.location.href = `/schedule`;
      }
    } catch (err) {
      notify({ type: 'error', message: err.message });
    }
    setLoading(false);
  };
  const LAYOUT = LAYOUTS[userType];

  const onSchedule = () => {
    setShowAssessmentConfirm(true);
  };

  const onJoin = () => {
    const appointment = appointments[0];
    const token = localStorage.getItem('token');
    window.open(
      `${appointment.twilio_web_app_url}&token=${token}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const isPatient = userType === USER_TYPES.patient.code;
  const showScheduleBtn = !loading && appointments.length === 0 && isPatient;

  const getPatientStepsContent = () => {
    const stateCode = currentUser.state;
    const stateConfig = STATE_ONBOARDING_STEPS[stateCode];
    if (!stateConfig) return null;

    return {
      stateName: stateConfig.name,
      newSteps: stateConfig.new,
      renewalSteps: stateConfig.renewal,
      fee: stateConfig.fee,
      cardDuration: stateConfig.cardDuration,
    };
  };

  const patientSteps = isPatient ? getPatientStepsContent() : null;

  return (
    <div className="DashView relative flex flex-col space-y-6">
      {loading && <LoadingOverlay show={loading} />}
      {showScheduleBtn && (
        <div className="bg-mmj-blue rounded-xl p-8 text-center">
          <h2 className="text-white text-2xl font-bold mb-2">
            Schedule Your Appointment
          </h2>
          <p className="text-white text-sm opacity-80 mb-5">
            Book your telehealth evaluation with an MMJ.com doctor.
          </p>
          <button
            className="w-full sm:w-auto bg-white text-mmj-blue font-bold py-4 px-10 rounded-lg text-lg hover:bg-gray-100 transition-colors"
            onClick={onSchedule}
          >
            Get Started
          </button>
        </div>
      )}
      {!loading && appointments[0] && (
        <div className="bg-mmj-blue rounded-xl text-white p-6">
          <div className="flex items-start space-x-3 mb-1">
            <div className="p-2 bg-indigo-800 rounded flex-shrink-0">
              <CalendarIcon className="h-6 w-6" />
            </div>
            <div>
              <div className="uppercase text-xs tracking-wider opacity-70">
                Upcoming Appointment
              </div>
              <div className="text-xl sm:text-2xl font-semibold">
                {convertToTimezone(
                  appointments[0].appointment_start_time,
                  currentUser.timezone,
                ).format('MMMM Do, YYYY')}
              </div>
              <div className="text-lg font-semibold opacity-90">
                {convertToTimezone(
                  appointments[0].appointment_start_time,
                  currentUser.timezone,
                ).format('h:mm a')}
              </div>
            </div>
          </div>
          {appointments[0].appointment_type === 'video' && (
            <button
              className="w-full sm:w-auto mt-4 button button-outline"
              onClick={onJoin}
            >
              Join Session
            </button>
          )}
          {isPatient && appointments[0].appointment_type === 'phone' && (
            <div className="mt-5 pt-5 border-t border-white border-opacity-20">
              <div className="font-semibold text-lg mb-3">
                Your Doctor Will Call You
              </div>
              <div className="space-y-3 text-sm opacity-90">
                <p>
                  Keep your phone line open at the scheduled time so the call
                  comes through.
                </p>
                <p>
                  If you have a call blocker for unknown numbers, please disable
                  it before your appointment. Your doctor may call from a
                  private or unrecognized number.
                </p>
                <p>
                  The call may not arrive at the exact appointment time. Please
                  allow up to 15 minutes.
                </p>
                <p>
                  If you have not received the call after 15 minutes, email{' '}
                  <a
                    href="mailto:support@MMJ.com"
                    className="underline font-semibold"
                  >
                    support@MMJ.com
                  </a>{' '}
                  and let us know.
                </p>
              </div>
            </div>
          )}
          {!isPatient && appointments[0].appointment_type === 'phone' && (
            <div className="mt-4 pt-4 border-t border-white border-opacity-20 text-sm opacity-90">
              Patient has requested a phone call.
            </div>
          )}
        </div>
      )}
      {isPatient && !loading && (
        <StateOnboardingSteps
          stateName={patientSteps ? patientSteps.stateName : null}
          newSteps={patientSteps ? patientSteps.newSteps : null}
          renewalSteps={patientSteps ? patientSteps.renewalSteps : null}
          fee={patientSteps ? patientSteps.fee : null}
          cardDuration={patientSteps ? patientSteps.cardDuration : null}
        />
      )}
      {LAYOUT &&
        LAYOUT.rows.map((_row) => (
          <div
            className={classNames([
              'DashView__rows__row flex flex-col lg:flex-row items-start space-y-4 lg:space-x-4 lg:space-y-0',
            ])}
            key={JSON.stringify(_row)}
          >
            {_row.cols.map((_col) => (
              <div
                className={`DashView__rows__col ${
                  _col.className ? _col.className : ''
                }`}
                key={JSON.stringify(_col)}
              >
                {_col.children}
              </div>
            ))}
          </div>
        ))}
      <ConfirmModal
        show={showAssessmentConfirm}
        onCancel={onCancelAssessment}
        onConfirm={onConfirmAssessment}
        title="Assessment"
        description="Have you taken a pre-assessment before?"
      />
    </div>
  );
};

export default DashView;
